<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useSwipe } from '@vueuse/core'
import { baseUrl, closePopup } from '@/helpers'
import { useSlideshowStore } from '@/stores/gallery/slideshow'

const SLIDE_ITEMS_PER_PAGE = 6

const props = defineProps({
  place: Number,
  category: Number,
})

const slideshowStore = useSlideshowStore()

// State -----------------------------------------
const animated = ref(false)
const page = ref(0)
const pageCount = ref(0)
const slider = ref(null)

// Getters ---------------------------------------
const items = computed(() =>
  slideshowStore.items
    .filter((e) => e.place == props.place && e.category == props.category)
    .map((e, i) => {
      e.index = i
      return e
    })
)

const activedItem = computed(() => items.value.find((e) => e.actived == true))
const hasBtnBack = computed(() => page.value > 1 )
const hasBtnNext = computed(() => page.value >= 1 && page.value < pageCount.value)

// Methods ---------------------------------------
function imgIcon(item) {
  const prefix = `${item.place}-${item.category}-${item.order}`
  return item.actived
    ? baseUrl(`/images/gallery/icon-hilight/${prefix}_M.png`)
    : baseUrl(`/images/gallery/icon/${prefix}_S.png`)
}

function slideToPage(_page) {
  if (pageCount.value > 1) {
    const targetPage = _page > pageCount.value ? pageCount.value : _page < 1 ? 1 : _page
    if (targetPage !== page.value) {
      const x = (targetPage - 1) * 150 * SLIDE_ITEMS_PER_PAGE
      slider.value.style.transition = '1s'
      slider.value.style.transform = `translateX(-${x}px)`
      page.value = targetPage
    }
  }
}

function slideToBackPage() {
  slideToPage(page.value - 1)
}

function slideToNextPage() {
  slideToPage(page.value + 1)
}

function slideToActiveItem() {
  const targetPage = Math.floor(activedItem.value.index / SLIDE_ITEMS_PER_PAGE) + 1
  if (page.value !== targetPage) {
    // console.debug('%c[GallerySlider] slideToActiveItem() page', style.debug, targetPage)
    slideToPage(targetPage)
  }
}

// Events ----------------------------------------
onMounted(() => {
  pageCount.value = Math.ceil(items.value.length / SLIDE_ITEMS_PER_PAGE)
  page.value = pageCount.value > 0 ? 1 : 0
  console.debug('%c[GallerySlider] onMounted', style.debug, { page: page.value, pageCount: pageCount.value })
  slideToActiveItem()
})

function onClickBtnBack() {
  slideToBackPage()
}

function onClickBtnNext() {
  slideToNextPage()
}

function onClickSlideItem(item) {  
  closePopup()
  slideshowStore.setActivedItem(item.id)
}

watch(activedItem, (newValue, oldValue) => {
  // console.log('watch activeItem', { newValue, oldValue })
  if (newValue !== undefined) {
    slideToActiveItem()
  }
})

const { isSwiping, direction } = useSwipe(slider)
watch(isSwiping, (newValue, oldValue) => {
  if (isSwiping.value) {
    if (direction.value == 'left') slideToNextPage()
    if (direction.value == 'right') slideToBackPage()
    console.log('watch direction', direction.value)
  }
})

// Main Setup ------------------------------------

// -----------------------------------------------
</script>

<template>
  <div class="gallery-slider" :place="place" :category="category" @transitionend="animated = false">
    <div v-if="items.length" class="gallery-slider-inner">
      <div class="slider-wrapper">
        <div ref="slider" class="slider">
          <transition-group name="fade-item">
            <img
              v-for="(item, i) in items"
              class="item"
              :key="imgIcon(item)"
              :src="imgIcon(item)"
              :style="{ left: `${i * 150}px` }"
              @click="onClickSlideItem(item)"
            />
          </transition-group>
        </div>
      </div>      
      <transition-group name="fade">
        <div v-show="hasBtnBack" key="back" class="btn-back" @click="onClickBtnBack"></div>
        <div v-show="hasBtnNext" key="next" class="btn-next" @click="onClickBtnNext"></div>
      </transition-group>      
    </div>
  </div>
</template>

<style scoped>
/* ------------------------ */
.fade-item-enter-active,
.fade-item-leave-active {
  transition: opacity 0.3s ease;
}
.fade-item-enter-from,
.fade-item-leave-to {
  opacity: 0;
}

/* ------------------------ */
.gallery-slider {
  display: block;
  height: 300px;
}

.gallery-slider-inner {
  height: 100%;
}

.slider-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 900px;
  height: 100%;
  overflow: hidden;
}

.slider .item {
  position: absolute;
  top: 0;
  cursor: pointer;
}

/* ------------------------ */
.btn-back {
  position: absolute;
  left: 25px;
  top: 70px;
  width: 50px;
  height: 150px;
  background: url('/images/arrow-back.png') no-repeat center;
  cursor: pointer;
}

.btn-next {
  position: absolute;
  left: 1007px;
  top: 70px;
  width: 50px;
  height: 150px;
  background: url('/images/arrow-next.png') no-repeat center;
  cursor: pointer;
}

/* ------------------------ */
</style>
